import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Power, Users, Briefcase, AlertTriangle, Info, AlertOctagon, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("dtile_admin_token")}` } });

const SEVERITY_META = {
  info: { icon: Info, label: "Info", color: "#1E2C5A", bg: "#EEF1FA" },
  warning: { icon: AlertTriangle, label: "Urgent", color: "#C9A968", bg: "#FDF8EC" },
  critical: { icon: AlertOctagon, label: "Critical", color: "#B23A3A", bg: "#FBECEC" },
};

const AUDIENCE_META = {
  buyers: { icon: Users, label: "Buyers" },
  employees: { icon: Briefcase, label: "Employees" },
  both: { icon: Megaphone, label: "Both" },
};

export default function AdminBroadcasts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [form, setForm] = useState({
    audience: "buyers",
    title: "",
    body: "",
    severity: "info",
    active: true,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/broadcasts?audience=${audienceFilter}`, auth());
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load broadcasts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [audienceFilter]);

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and message body are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/broadcasts`, form, auth());
      toast.success("Announcement published");
      setForm({ audience: "buyers", title: "", body: "", severity: "info", active: true });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (b) => {
    try {
      await axios.patch(`${API}/admin/broadcasts/${b.id}`, { active: !b.active }, auth());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete announcement "${b.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/broadcasts/${b.id}`, auth());
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6" data-testid="admin-broadcasts">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#8a8a8a] inline-flex items-center gap-1.5">
            <Megaphone className="w-3.5 h-3.5" strokeWidth={1.5} /> Announcements
          </p>
          <h2 className="font-editorial text-3xl mt-1 text-[#1A1A1A]">Urgent Broadcast Messages</h2>
          <p className="text-sm text-[#5C5C5C] mt-2 max-w-xl">
            Compose a message that appears as a modal the moment a buyer or employee signs in. Perfect for warehouse shutdowns, price revisions, mandatory meetings, or festive greetings.
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 text-white text-[11px] tracking-[0.2em] uppercase"
          style={{ background: "#1E2C5A" }}
          data-testid="broadcast-add-toggle"
        >
          {showForm ? <><X className="w-3.5 h-3.5" strokeWidth={1.5} /> Close</> : <><Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> New Announcement</>}
        </button>
      </div>

      {showForm && (
        <div className="border border-[#e5e0d0] p-6 mb-6 bg-[#FAFAF7]" data-testid="broadcast-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#8a8a8a] block mb-2">Audience</label>
              <select
                className="w-full border border-[#d9d3bf] px-3 py-2 text-sm bg-white"
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                data-testid="broadcast-audience"
              >
                <option value="buyers">Buyers only</option>
                <option value="employees">Employees only</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#8a8a8a] block mb-2">Severity</label>
              <select
                className="w-full border border-[#d9d3bf] px-3 py-2 text-sm bg-white"
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value })}
                data-testid="broadcast-severity-select"
              >
                <option value="info">Info · navy</option>
                <option value="warning">Urgent · gold</option>
                <option value="critical">Critical · red</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#8a8a8a] block mb-2">Status</label>
              <button
                type="button"
                onClick={() => setForm({ ...form, active: !form.active })}
                className={`w-full border px-3 py-2 text-sm ${form.active ? "border-[#10B981] text-[#10B981]" : "border-[#d9d3bf] text-[#8a8a8a]"}`}
                data-testid="broadcast-active-toggle"
              >
                {form.active ? "Active · will show on sign-in" : "Draft · hidden"}
              </button>
            </div>
          </div>
          <label className="text-[10px] tracking-[0.25em] uppercase text-[#8a8a8a] block mb-2">Title</label>
          <input
            className="w-full border border-[#d9d3bf] px-3 py-2 text-sm mb-4 bg-white"
            placeholder="e.g. Warehouse Diwali Shutdown"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="broadcast-title-input"
          />
          <label className="text-[10px] tracking-[0.25em] uppercase text-[#8a8a8a] block mb-2">Message body</label>
          <textarea
            rows={5}
            className="w-full border border-[#d9d3bf] px-3 py-2 text-sm mb-4 bg-white resize-none"
            placeholder="Dispatch will resume on 4th Nov. Please place any urgent orders by 30th Oct."
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            data-testid="broadcast-body-input"
          />
          <div className="text-right">
            <button
              onClick={submit}
              disabled={saving}
              className="px-6 py-2 text-white text-[11px] tracking-[0.2em] uppercase disabled:opacity-50"
              style={{ background: "#C9A968" }}
              data-testid="broadcast-submit"
            >
              {saving ? "Publishing…" : "Publish"}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <span className="text-[10px] tracking-[0.25em] uppercase text-[#8a8a8a]">Filter:</span>
        {["all", "buyers", "employees"].map((k) => (
          <button
            key={k}
            onClick={() => setAudienceFilter(k)}
            className={`text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 border transition-colors ${audienceFilter === k ? "bg-[#1E2C5A] text-white border-[#1E2C5A]" : "border-[#d9d3bf] text-[#5C5C5C]"}`}
            data-testid={`broadcast-filter-${k}`}
          >
            {k}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center py-12 text-[10px] tracking-[0.25em] uppercase text-[#8a8a8a]">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[#d9d3bf] p-12 text-center">
          <Megaphone className="w-8 h-8 mx-auto mb-3 text-[#C9A968]" strokeWidth={1.5} />
          <p className="text-sm text-[#5C5C5C]">No announcements yet.</p>
          <p className="text-xs text-[#8a8a8a] mt-1">Click "New Announcement" to compose one.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="broadcast-list">
          {rows.map((b) => {
            const sev = SEVERITY_META[b.severity] || SEVERITY_META.info;
            const aud = AUDIENCE_META[b.audience] || AUDIENCE_META.buyers;
            const Icon = sev.icon;
            const AudIcon = aud.icon;
            return (
              <div
                key={b.id}
                className="border border-[#e5e0d0] bg-white flex flex-col md:flex-row"
                data-testid={`broadcast-row-${b.id}`}
              >
                <div style={{ background: sev.bg, borderLeft: `4px solid ${sev.color}` }} className="p-4 md:w-64 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" style={{ color: sev.color }} strokeWidth={1.5} />
                    <span className="text-[10px] tracking-[0.25em] uppercase font-medium" style={{ color: sev.color }}>{sev.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-[#5C5C5C]">
                    <AudIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> {aud.label}
                  </div>
                  <p className="text-[10px] text-[#8a8a8a] mt-2">{new Date(b.created_at).toLocaleString()}</p>
                </div>
                <div className="p-4 flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="font-editorial text-lg text-[#1A1A1A] break-words">{b.title}</h4>
                      <p className="text-sm text-[#3A3A3A] mt-1 whitespace-pre-wrap break-words">{b.body}</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 flex md:flex-col gap-2 items-center md:items-end justify-end border-t md:border-t-0 md:border-l border-[#e5e0d0]">
                  <button
                    onClick={() => toggle(b)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase border ${b.active ? "border-[#10B981] text-[#10B981]" : "border-[#8a8a8a] text-[#8a8a8a]"}`}
                    data-testid={`broadcast-toggle-${b.id}`}
                  >
                    <Power className="w-3 h-3" strokeWidth={1.5} />
                    {b.active ? "Active" : "Off"}
                  </button>
                  <button
                    onClick={() => remove(b)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase border border-[#B23A3A] text-[#B23A3A]"
                    data-testid={`broadcast-delete-${b.id}`}
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
